import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Key, Copy, Check, Trash2, Plus, Shield, RefreshCcw, Gauge } from "lucide-react";
import { toast } from "sonner";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [allowedIps, setAllowedIps] = useState("");
  const [rlCap, setRlCap] = useState("");
  const [rlRefill, setRlRefill] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editIps, setEditIps] = useState("");
  const [editCap, setEditCap] = useState("");
  const [editRefill, setEditRefill] = useState("");

  const load = async () => {
    const { data } = await api.get("/agent/keys");
    setKeys(data);
  };
  useEffect(() => { load(); }, []);

  const parseIps = (s) =>
    s.split(/[,\n\s]+/).map((x) => x.trim()).filter(Boolean);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const body = { name: name.trim() };
      const ips = parseIps(allowedIps);
      if (ips.length) body.allowed_ips = ips;
      if (rlCap) body.rate_limit_capacity = Number(rlCap);
      if (rlRefill) body.rate_limit_refill_per_sec = Number(rlRefill);
      const { data } = await api.post("/agent/keys", body);
      setJustCreated(data);
      setName(""); setAllowedIps(""); setRlCap(""); setRlRefill("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key");
    } finally { setCreating(false); }
  };

  const rotate = async (id) => {
    if (!confirm("Rotate this key? A new token will be issued and the old key stays valid for 24h.")) return;
    try {
      const { data } = await api.post(`/agent/keys/${id}/rotate`, { grace_seconds: 24 * 3600 });
      setJustCreated(data);
      toast.success("Rotated — old key expires in 24h");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rotate failed");
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this key? External tools using it will stop working immediately.")) return;
    await api.delete(`/agent/keys/${id}`);
    toast.success("Revoked");
    load();
  };

  const openEdit = (k) => {
    setEditing(k);
    setEditIps((k.allowed_ips || []).join("\n"));
    setEditCap(k.rate_limit_capacity != null ? String(k.rate_limit_capacity) : "");
    setEditRefill(k.rate_limit_refill_per_sec != null ? String(k.rate_limit_refill_per_sec) : "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = { allowed_ips: parseIps(editIps) };
      body.rate_limit_capacity = editCap === "" ? 0 : Number(editCap);
      body.rate_limit_refill_per_sec = editRefill === "" ? 0 : Number(editRefill);
      await api.patch(`/agent/keys/${editing.id}`, body);
      toast.success("Key updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const copyToken = async () => {
    if (!justCreated) return;
    await navigator.clipboard.writeText(justCreated.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-8 max-w-4xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Agent API</p>
        <h1 className="font-display text-3xl">Keys for external tools</h1>
        <p className="mt-2 text-muted-foreground">
          Workspace-scoped, read-only. Give one to Zapier / n8n / your custom agent and hit
          <code className="font-mono text-xs bg-secondary/60 rounded px-1.5 py-0.5 mx-1">POST /api/agent/query</code>
          with <code className="font-mono text-xs bg-secondary/60 rounded px-1.5 py-0.5">Authorization: Bearer &lt;key&gt;</code>.
        </p>
      </header>

      <form onSubmit={create} className="card-flat p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="key-name">Key name</Label>
            <Input
              id="key-name"
              placeholder="e.g. n8n production, Zapier support bot"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="apikeys-name-input"
            />
          </div>
          <div className="flex md:items-end">
            <Button type="submit" disabled={creating || !name.trim()} className="rounded-full w-full md:w-auto" data-testid="apikeys-create-btn">
              <Plus className="w-4 h-4 mr-2" /> {creating ? "Creating…" : "Create key"}
            </Button>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="key-ips" className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Allowed IPs / CIDRs
              <span className="text-[10px] text-muted-foreground font-normal">optional — defense in depth</span>
            </Label>
            <Input
              id="key-ips"
              placeholder="10.0.0.0/8, 203.0.113.5"
              value={allowedIps}
              onChange={(e) => setAllowedIps(e.target.value)}
              data-testid="apikeys-ips-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="key-cap" className="flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" /> Burst capacity
              <span className="text-[10px] text-muted-foreground font-normal">optional — per-key</span>
            </Label>
            <Input
              id="key-cap"
              type="number"
              min="1"
              placeholder="60"
              value={rlCap}
              onChange={(e) => setRlCap(e.target.value)}
              data-testid="apikeys-cap-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="key-refill" className="flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" /> Refill / sec
              <span className="text-[10px] text-muted-foreground font-normal">optional — tokens/sec</span>
            </Label>
            <Input
              id="key-refill"
              type="number"
              step="0.1"
              min="0"
              placeholder="1.0"
              value={rlRefill}
              onChange={(e) => setRlRefill(e.target.value)}
              data-testid="apikeys-refill-input"
            />
          </div>
        </div>
      </form>

      {keys.length === 0 ? (
        <div className="card-flat p-10 text-center text-muted-foreground text-sm" data-testid="apikeys-empty">
          <Key className="w-6 h-6 mx-auto mb-3 opacity-60" /> No keys yet. Create one above.
        </div>
      ) : (
        <div className="card-flat divide-y divide-border/60">
          {keys.map((k) => (
            <div key={k.id} className="p-4 flex items-center gap-4" data-testid={`apikey-row-${k.id}`}>
              <Key className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{k.name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {k.token_prefix} · created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : " · never used"}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground font-mono mt-1">
                  <span>{k.use_count || 0} calls</span>
                  {k.avg_latency_ms != null && <span>avg {Math.round(k.avg_latency_ms)}ms</span>}
                  {k.p95_latency_ms != null && <span>p95 {Math.round(k.p95_latency_ms)}ms</span>}
                  {k.last_latency_ms != null && <span>last {Math.round(k.last_latency_ms)}ms</span>}
                  {k.last_ip && <span>ip {k.last_ip}</span>}
                  {(k.rate_limit_capacity || k.rate_limit_refill_per_sec) && (
                    <span data-testid={`apikey-ratelimit-${k.id}`}>
                      rl {k.rate_limit_capacity || "—"}·{k.rate_limit_refill_per_sec || "—"}/s
                    </span>
                  )}
                </div>
                {(k.rotated_at || k.revoke_at) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-amber-600 dark:text-amber-400 font-mono mt-1" data-testid={`apikey-rotation-${k.id}`}>
                    {k.rotated_at && <span>rotated {new Date(k.rotated_at).toLocaleString()}</span>}
                    {k.revoke_at && <span>expires {new Date(k.revoke_at).toLocaleString()}</span>}
                  </div>
                )}
                {k.allowed_ips && k.allowed_ips.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <Shield className="w-3 h-3 text-muted-foreground" />
                    {k.allowed_ips.map((ip) => (
                      <span key={ip} className="text-[10px] font-mono border border-border/60 rounded px-1.5 py-0.5">{ip}</span>
                    ))}
                  </div>
                )}
              </div>
              {k.revoked ? (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Revoked</span>
              ) : (
                <>
                  <button
                    onClick={() => openEdit(k)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Edit whitelist + rate limit"
                    data-testid={`apikey-edit-${k.id}`}
                  >
                    <Shield className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => rotate(k.id)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Rotate key (24h grace)"
                    data-testid={`apikey-rotate-${k.id}`}
                  >
                    <RefreshCcw className="w-4 h-4" />
                  </button>
                  <button onClick={() => revoke(k.id)} className="text-muted-foreground hover:text-destructive" data-testid={`apikey-revoke-${k.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!justCreated} onOpenChange={(o) => !o && setJustCreated(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Key created</DialogTitle>
            <DialogDescription>Copy this key now — it will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="p-3 rounded-md bg-secondary/60 font-mono text-xs break-all border border-border/60" data-testid="apikey-token-display">
              {justCreated?.token}
            </div>
            <Button onClick={copyToken} variant="outline" className="rounded-full w-full" data-testid="apikey-copy-btn">
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy to clipboard</>}
            </Button>
            <div className="text-xs text-muted-foreground border-t border-border/60 pt-3">
              <p className="mb-2">Example curl:</p>
              <pre className="p-3 bg-secondary/40 rounded-md overflow-auto font-mono text-[10px] leading-relaxed">
{`curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/agent/query \\
  -H "Authorization: Bearer ${justCreated?.token?.slice(0, 12)}…" \\
  -H "Content-Type: application/json" \\
  -d '{"question": "How do we handle refunds?", "top_k": 5}'`}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Shield className="w-4 h-4" /> Key configuration
            </DialogTitle>
            <DialogDescription>
              Restrict source IPs (CIDR OK) and tune the per-key token bucket. Leave a field empty to clear it.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Allowed IPs / CIDRs
              </Label>
              <Textarea
                rows={4}
                placeholder={"One per line, e.g.\n10.0.0.0/8\n203.0.113.5\n2001:db8::/32"}
                value={editIps}
                onChange={(e) => setEditIps(e.target.value)}
                data-testid="apikey-edit-ips-input"
                className="font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-cap" className="flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5" /> Burst capacity
                </Label>
                <Input
                  id="edit-cap"
                  type="number"
                  min="0"
                  placeholder="60"
                  value={editCap}
                  onChange={(e) => setEditCap(e.target.value)}
                  data-testid="apikey-edit-cap-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-refill" className="flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5" /> Refill / sec
                </Label>
                <Input
                  id="edit-refill"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="1.0"
                  value={editRefill}
                  onChange={(e) => setEditRefill(e.target.value)}
                  data-testid="apikey-edit-refill-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="rounded-full" data-testid="apikey-edit-save-btn">Save</Button>
              <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
